import { graphql } from 'gatsby';

import { VideoWithCaption } from '@mewa/ui';

import { responsiveVideoPopulate } from '../../../../libs/strapi-populates';
import {
  ConfigType,
  ContentSectionItem,
  PossibleContentSections,
} from '../fragment-and-types';
import { ContentLivePreviewConfig } from '../live-preview/types';
import { StrapiImage, StrapiResponsiveImageData } from './image';
import { ResponsiveVideoData } from './video/types';
import { gatsbyVideoDataFromStrapiVideo } from './video/video';

export const query = graphql`
  fragment VideoWithCaptionFragment on STRAPI__COMPONENT_SHARED_VIDEO_WITH_CAPTION {
    video: Video {
      ...ResponsiveVideoFragment
    }
    caption: Caption
    strapi_component
  }
`;

export const componentName = 'shared.video-with-caption';

export type VideoWithCaptionData = {
  video: ResponsiveVideoData;
  caption?: string | null;
  strapi_component: typeof componentName;
};

type VideoAttributes = {
  name: string;
  alternativeText: string | null;
  caption: string | null;
  mime: string;
  url: string;
  fields: { assetUrl: string };
};

export type VideoWithCaptionPreviewData = {
  Video: {
    VideoDesktop: VideoAttributes;
    VideoMobile: VideoAttributes;
    PosterImage?: StrapiResponsiveImageData | null;
    AutoPlay: boolean;
    strapi_component: 'shared.responsive-video';
  };
  Caption?: string | null;
  strapi_component: typeof componentName;
};

export const isComponent = (
  component: PossibleContentSections,
): component is ContentSectionItem<VideoWithCaptionData> =>
  component.strapi_component === componentName;

const Component = (props: VideoWithCaptionData) => (
  <VideoWithCaption
    autoPlay={props.video.autoPlay}
    desktopSources={gatsbyVideoDataFromStrapiVideo(props.video.videoDesktop)}
    mobileSources={gatsbyVideoDataFromStrapiVideo(props.video.videoMobile)}
    posterImage={
      props.video.posterImage ? (
        <StrapiImage responsiveImage={props.video.posterImage} />
      ) : null
    }
    caption={props.caption}
  />
);

export const config: ConfigType<VideoWithCaptionData> = {
  componentName,
  isComponent,
  Component,
};

export const previewConfig: ContentLivePreviewConfig = {
  componentName,
  populationParams: {
    Video: responsiveVideoPopulate,
  },
  mapProperties: (data) =>
    data.strapi_component === componentName
      ? {
          ...data,
          video: {
            videoDesktop: data.Video.VideoDesktop,
            videoMobile: data.Video.VideoMobile,
            posterImage: data.Video.PosterImage,
            autoPlay: data.Video.AutoPlay,
            strapi_component: data.Video.strapi_component,
          },
          caption: data.Caption,
        }
      : null,
};
